import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Button } from './button';
import { Dialog } from './dialog';

type Tone = 'danger' | 'default';

type ConfirmOptions = {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: Tone;
};

type ConfirmContextValue = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

type Pending = {
  opts: ConfirmOptions;
  resolve: (value: boolean) => void;
};

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const resolveRef = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setPending({ opts, resolve });
    });
  }, []);

  function close(answer: boolean) {
    pending?.resolve(answer);
    setPending(null);
  }

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Dialog
        open={pending != null}
        onClose={() => close(false)}
        title={pending?.opts.title ?? ''}
      >
        {pending?.opts.body ? (
          <div className="text-sm text-[var(--color-foreground-muted)]">{pending.opts.body}</div>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => close(false)}>
            {pending?.opts.cancelLabel ?? 'Cancel'}
          </Button>
          <Button
            variant={pending?.opts.tone === 'danger' ? 'danger' : 'primary'}
            onClick={() => close(true)}
          >
            {pending?.opts.confirmLabel ?? 'Confirm'}
          </Button>
        </div>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmContextValue {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm() must be called within <ConfirmProvider>');
  }
  return ctx;
}
